import React from "react";
import styled from "styled-components";
import { Flex } from "antd";
import { useTranslate } from "@refinedev/core";
import { InfoTooltip, Skeleton, radius } from "@libertify/ui";
import {
  getMetricLevel,
  metricColors,
  formatDuration,
  formatPct,
  type MetricLevel,
} from "./utils/";
import { analyticsTheme } from "./analyticsTheme";

const T_PREFIX = "media.projects.analytics.dashboard.summaryCards";

type MetricType = "number" | "percentage" | "seconds" | "minutes";

const formatters: Record<MetricType, (v: number) => string> = {
  number: (v) => v.toLocaleString(),
  percentage: (v) => formatPct(v),
  seconds: (v) => `${Math.round(v)}s`,
  minutes: formatDuration,
};

// Each color tier maps to a plain-language status (none when unscored).
const STATUS_KEY: Record<MetricLevel, string | null> = {
  success: "statusOnTrack",
  warning: "statusBelowTarget",
  error: "statusOffTrack",
  default: null,
};

export type CompactStatCardProps = {
  label: string;
  metric?: number | null;
  type?: MetricType;
  /** Shown before the value, e.g. "Q7 ·" for "Q7 · 33%". */
  prefix?: string;
  suffix?: string;
  target?: number;
  aim?: "high" | "low";
  loading?: boolean;
  tooltip?: string;
  /** Context line under the value, e.g. "by 4 viewers". */
  subtitle?: string;
  /** Drops the shadow, for cards sitting inside another card. */
  flat?: boolean;
  /** Overrides the value color set by the target. */
  valueColor?: string;
  /** Shown next to the label, e.g. a tag saying what the metric counts. */
  badge?: React.ReactNode;
};

export const CompactStatCard: React.FC<CompactStatCardProps> = ({
  label,
  metric,
  type = "number",
  prefix,
  suffix,
  target,
  aim = "high",
  loading,
  tooltip,
  subtitle,
  flat,
  valueColor,
  badge,
}) => {
  const t = useTranslate();
  const formattedMetric =
    metric == null || Number.isNaN(metric)
      ? null
      : [prefix, formatters[type](metric), suffix].filter(Boolean).join(" ");
  const displayValue = formattedMetric ?? "--";
  const level = getMetricLevel(metric ?? undefined, target, aim);
  const color = metricColors[level];

  // Plain-language verdict, shown only when the metric is actually scored
  // against a target.
  const statusKey = formattedMetric == null ? null : STATUS_KEY[level];
  const statusLabel = statusKey ? t(`${T_PREFIX}.${statusKey}`) : null;

  if (loading) {
    return (
      <StyledCard $flat={flat}>
        <Skeleton variant="text" rows={1} height={10} width="50%" />
        <div style={{ marginTop: 8 }}>
          <Skeleton variant="rectangular" height={32} width="60%" />
        </div>
      </StyledCard>
    );
  }

  return (
    <StyledCard $flat={flat}>
      <Flex align="center" justify="space-between" style={{ marginBottom: 8 }}>
        <Flex align="center" wrap gap={8}>
          <Label>{label}</Label>
          {badge}
        </Flex>
        {tooltip && <InfoTooltip content={tooltip} />}
      </Flex>
      <Value $color={valueColor ?? color}>{displayValue}</Value>
      {statusLabel ? <Status $color={color}>{statusLabel}</Status> : null}
      {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
    </StyledCard>
  );
};

// ─── Styled components ───

// Fills its grid cell so cards in a row share one height, with or without
// a status or subtitle. border-box keeps the padding inside that cell.
const StyledCard = styled.div<{ $flat?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border: 1px solid ${analyticsTheme.cardBorder};
  border-radius: ${radius.box}px;
  padding: 16px 20px;
  background: ${analyticsTheme.cardBg};
  box-shadow: ${({ $flat }) => ($flat ? "none" : analyticsTheme.cardShadow)};
`;

const Label = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${analyticsTheme.textMuted};
`;

const Value = styled.div<{ $color: string }>`
  font-size: 26px;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ $color }) => $color};
`;

// Plain-language verdict shown under the value so the color isn't the only
// (and ambiguous) signal — the word says where the value stands vs its goal.
const Status = styled.div<{ $color: string }>`
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.4;
  font-weight: 600;
  color: ${({ $color }) => $color};
`;

const Subtitle = styled.div`
  margin-top: 6px;
  font-size: 12.5px;
  line-height: 1.4;
  color: ${analyticsTheme.textMuted};
`;
